/**
 * Service to handle communication with the main nCinq repository.
 * Interface requirements defined in REQUIREMENTS.md
 */

const API_BASE_URL = import.meta.env.VITE_NCINQ_API_URL || '';
const API_KEY = import.meta.env.VITE_NCINQ_API_KEY;

const headers = {
  'Content-Type': 'application/json',
  'X-nCinq-API-Key': API_KEY,
};

export const ncinqApiService = {
  /**
   * Initiates an automated MMG payment flow.
   * @param {Object} data - { amount, description, customerName, customerEmail, customerPhone, invoiceId, callbackUrl, redirectUrl }
   * @returns {Promise<Object>} { success, checkoutUrl, orderId }
   */
  async initiateCheckout(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/external/checkout/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Checkout failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('External API Checkout Error:', error);
      throw error;
    }
  },

  /**
   * Fetches global profile information for an nCinq user.
   * @param {string} email
   * @returns {Promise<Object>} { displayName, role, photoURL, bio }
   */
  async fetchUserProfile(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/external/user/?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('External API User Profile Error:', error);
      return null;
    }
  },

  /**
   * Fetches purchase history for an email across the nCinq ecosystem.
   * @param {string} email
   * @returns {Promise<Array>} List of orders
   */
  async fetchUserOrders(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/external/orders/?email=${encodeURIComponent(email)}&limit=50`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.orders || [];
    } catch (error) {
      console.error('External API Orders History Error:', error);
      return [];
    }
  },

  /**
   * Fetches active e-Tickets and digital assets.
   * @param {string} email
   * @returns {Promise<Array>} List of tickets
   */
  async fetchUserTickets(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/external/tickets/?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.tickets || [];
    } catch (error) {
      console.error('External API Tickets Error:', error);
      return [];
    }
  },
};
